// The Grid editor's edits, as rule algebra (no React): paint one cell, switch
// a whole row or column. Built ONLY from the shared twin primitives of
// uda/rules.ts (addCell / removeCell / addRule / canonRule / accepts / minus /
// normalize) — test-script/test_uda_setup_ui.mjs applies the same operations
// with rules.py and requires the very same rules.
//
// A row (or column) switched OFF removes the whole slice — every value of the
// other axis, including values the master gains later — instead of removing
// today's cells one by one, which would leave an "all but these" remainder
// covering nobody yet but every future value.

import type { UdaRule } from '../types'
import {
  accepts, addCell, addRule, canonRule, codeOf, driverKeys, minus, normalize, removeCell,
  setCovers, type CanonRule,
} from '../rules'

export interface GridAxes {
  rows: string
  cols: string | null
  page: string | null
}

type Drivers = Parameters<typeof addCell>[2]

/** The combination a cell stands for. */
export function cellCombo(axes: GridAxes, row: string, col: string | null, pageValue: string | null): Record<string, string> {
  const c: Record<string, string> = { [axes.rows]: row }
  if (axes.cols && col !== null) c[axes.cols] = col
  if (axes.page && pageValue !== null) c[axes.page] = pageValue
  return c
}

/** Paint: add the cell to the set's rules, or take it out. */
export function paintCell(rules: UdaRule[] | undefined, combo: Record<string, string>, drivers: Drivers,
  add: boolean): CanonRule[] {
  return add ? addCell(rules || [], combo, drivers) : removeCell(rules || [], combo, drivers)
}

/** Every rule without the combinations `pin` names (some drivers pinned to
 *  one value, the rest left open): the rectangle split of removeCell applied
 *  to the pinned drivers only. Pinning every driver is removeCell. */
export function removeSlice(rules: UdaRule[] | undefined, pin: Record<string, string>, drivers: Drivers): CanonRule[] {
  const keys = driverKeys(drivers)
  const pinned = keys.filter(k => k in pin)
  const out: CanonRule[] = []
  for (const raw of rules || []) {
    const rule = canonRule(raw, keys)
    const hits = pinned.every(k => accepts(rule[k], codeOf(pin[k])))
    if (!hits) { out.push(rule); continue }
    const prefix: CanonRule = { ...rule }
    for (const k of pinned) {
      const rest = minus(rule[k], String(codeOf(pin[k])))
      if (!(Array.isArray(rest) && rest.length === 0)) out.push({ ...prefix, [k]: rest })
      prefix[k] = [String(codeOf(pin[k]))]
    }
  }
  return normalize(out, keys)
}

function sliceCells(axes: GridAxes, along: 'row' | 'col', value: string, others: string[],
  pageValue: string | null): Array<Record<string, string>> {
  if (along === 'row') {
    return axes.cols
      ? others.map(cv => cellCombo(axes, value, cv, pageValue))
      : [cellCombo(axes, value, null, pageValue)]
  }
  return others.map(rv => cellCombo(axes, rv, value, pageValue))
}

/**
 * A row / column heading click while painting (prototype `rowtoggle` /
 * `coltoggle`): when the set already covers every cell of the slice (over
 * the other axis's known values), the slice is removed; otherwise one rule
 * covering the slice is added — the other axis Any, the page pinned.
 */
export function toggleSlice(rules: UdaRule[] | undefined, axes: GridAxes, along: 'row' | 'col', value: string,
  otherValues: string[], pageValue: string | null, drivers: Drivers): CanonRule[] {
  const keys = driverKeys(drivers)
  const cells = sliceCells(axes, along, value, otherValues, pageValue)
  const allCovered = cells.length > 0 && cells.every(c => setCovers(rules || [], c, keys))
  const axisKey = along === 'row' ? axes.rows : axes.cols
  if (!axisKey) return normalize(rules || [], keys)
  const pin: Record<string, string> = { [axisKey]: value }
  if (axes.page && pageValue !== null) pin[axes.page] = pageValue
  if (allCovered) return removeSlice(rules, pin, keys)
  const rule: Record<string, unknown> = {}
  for (const k of keys) rule[k] = k in pin ? [pin[k]] : '*'
  return addRule(rules || [], rule, keys)
}
